// ─── Map Marker Icon Factories ──────────────────────────────────────────
// Pure functions that produce L.DivIcon instances for map markers.
// No state, no side-effects — just data in, icon out.
// Consumed by MapRenderer rendering methods.
// ─────────────────────────────────────────────────────────────────────────

import L from "leaflet";
import {
  POI_COLORS,
  ACTION_COLORS,
  NEUTRAL,
  iconElement,
} from "../data/design-tokens";

/** Escape text at Leaflet's HTML boundary, including attribute delimiters. */
export function escapeHtml(value: string): string {
  return value.replaceAll(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
}

// ─── Route Station Marker ───────────────────────────────────────────────
// Dots along a search route or trip — endpoints are filled, intermediates
// are white with a colored ring. Optional food/overnight badges + POI gem.

export function createRouteMarker(
  color: string,
  isEndpoint: boolean,
  poiType: string | null | undefined,
  isSelected: boolean,
  foodStop: boolean,
  overnightStop: boolean
): L.DivIcon {
  const intermediateSize = isSelected ? 16 : 12;
  const size = isEndpoint ? 18 : intermediateSize;
  const border = isEndpoint ? 3 : 2;

  let badgeHtml = "";
  if (overnightStop) {
    badgeHtml = `<div style="position:absolute;top:-10px;right:-10px;background:${ACTION_COLORS.overnight};color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:11px;box-shadow:0 1px 3px rgba(0,0,0,0.3);border:1.5px solid white;">&#9789;</div>`;
  }
  if (foodStop) {
    const offset = overnightStop ? "left:-10px" : "right:-10px";
    badgeHtml += `<div style="position:absolute;top:-10px;${offset};background:${ACTION_COLORS.food};color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;box-shadow:0 1px 3px rgba(0,0,0,0.3);border:1.5px solid white;">&#127860;</div>`;
  }

  let gemHtml = "";
  if (!isEndpoint && poiType) {
    const bg = POI_COLORS[poiType as keyof typeof POI_COLORS];
    const sym = iconElement(poiType, "svg");
    if (bg && sym) {
      gemHtml = `<div style="position:absolute;bottom:-5px;right:-5px;background:${bg};border-radius:50%;width:14px;height:14px;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 2px rgba(0,0,0,0.3);border:1px solid white;">${sym}</div>`;
    }
  }

  const pulseHtml = isSelected
    ? `<div style="position:absolute;top:${-4}px;left:${-4}px;width:${size + 8}px;height:${size + 8}px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:marker-pulse 1.5s ease-out infinite;"></div>`
    : "";

  const bg = isEndpoint ? color : "white";
  const borderColor = isEndpoint ? "white" : color;

  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative;width:${size}px;height:${size}px;">
        ${pulseHtml}
        <div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:${border}px solid ${borderColor};box-shadow:0 1px 4px rgba(0,0,0,0.3);position:relative;z-index:2;"></div>
        ${gemHtml}
        ${badgeHtml}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// ─── Day Badge ──────────────────────────────────────────────────────────
// Floating "Day N" pill placed at overnight-stop boundaries on the map.

export function createDayBadge(day: number, color: string): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:white;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.25);border:2px solid white;letter-spacing:0.3px;">Day ${day}</div>`,
    iconSize: [60, 24],
    iconAnchor: [-8, 12],
  });
}

// ─── Pinned Stop Card Marker ────────────────────────────────────────────
// Card-shaped DivIcon for saved Food/Overnight/Fika stops on the map.
// Shows city name, day+time, action icons, and a colored anchor dot.

export interface PinnedStopOpts {
  cityName: string;
  /** One time, or separate arrival/departure lines with their day numbers. */
  timeLabel: string;
  dayColor: string;
  actions: { food: boolean; overnight: boolean; fika: boolean };
  isSelected: boolean;
  /** Horizontal icon-anchor offset in px (+ve → card shifts left). Default 0. */
  anchorXOffset?: number;
}

export function createPinnedStopMarker(opts: PinnedStopOpts): L.DivIcon {
  let ringColor: string = ACTION_COLORS.default;
  if (opts.actions.overnight) ringColor = ACTION_COLORS.overnight;
  else if (opts.actions.food) ringColor = ACTION_COLORS.food;
  else if (opts.actions.fika) ringColor = ACTION_COLORS.fika;

  const parts: string[] = [];
  if (opts.actions.overnight) parts.push(iconElement("overnight", "svg", true));
  if (opts.actions.fika) parts.push(iconElement("fika", "svg", true));
  if (opts.actions.food) parts.push(iconElement("food", "svg", true));
  const iconStr = parts.join(" ");
  const dotSize = 14;
  const cardWidth = 120;
  const cardHeight = 80;
  const anchorX = cardWidth / 2 + (opts.anchorXOffset ?? 0);

  const html = `
    <div class="pinned-card${opts.isSelected ? " is-selected" : ""}" style="position:relative;width:${cardWidth}px;height:${cardHeight}px;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;">
      <div style="background:white;border-radius:8px;padding:5px 8px;box-shadow:0 2px 8px rgba(0,0,0,0.18);border:1px solid rgba(0,0,0,0.08);width:${cardWidth}px;pointer-events:auto;margin-bottom:4px;">
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="font-size:11px;font-weight:600;color:${NEUTRAL.title};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;">${escapeHtml(opts.cityName)}</span>
          <span style="font-size:12px;flex-shrink:0;">${iconStr}</span>
        </div>
        ${opts.timeLabel ? `<div style="font-size:9px;font-weight:600;color:${opts.dayColor};margin-top:2px;white-space:pre-line;">${escapeHtml(opts.timeLabel)}</div>` : ""}
      </div>
      <div style="position:relative;flex-shrink:0;">
        <div class="pulse-ring" style="position:absolute;bottom:0;left:50%;transform:translateX(-50%);width:${dotSize + 10}px;height:${dotSize + 10}px;border-radius:50%;border:2px solid ${ringColor};opacity:0.4;pointer-events:none;"></div>
        <div style="width:${dotSize}px;height:${dotSize}px;box-sizing:border-box;border-radius:50%;background:white;border:3px solid ${ringColor};box-shadow:0 0 0 3px ${ringColor}40,0 2px 6px rgba(0,0,0,0.3);position:relative;z-index:2;"></div>
      </div>
    </div>
  `;

  return L.divIcon({
    className: "",
    html,
    iconSize: [cardWidth, cardHeight],
    iconAnchor: [anchorX, cardHeight - dotSize / 2],
  });
}

// ─── POI Dot Marker ─────────────────────────────────────────────────────
// Small colored circle with an icon for explore/search POIs.
// Cached — the same (color, symbol) pair always produces an identical icon.

const _poiDotCache = new Map<string, L.DivIcon>();

export function createPOIDot(
  color: string,
  symbol: string
): L.DivIcon {
  const key = `${color}|${symbol}`;
  let icon = _poiDotCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      className: "",
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      html: `<div style="
        width:20px;height:20px;border-radius:50%;
        background:${color};border:2px solid white;
        box-shadow:0 1px 4px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
      ">${symbol}</div>`,
    });
    _poiDotCache.set(key, icon);
  }
  return icon;
}
